<template>
    <div>
        <Head>
            <meta name="description" :content="property.meta_tag">
        </Head>
        <Head v-if="property.is_agent_fee || is_archived">
            <meta name="robots" content="noindex,nofollow">
        </Head>
        <property-layout :title="'For Rent: '+property.full_address">
            <PropertyListing :property="property" :inactive="application_inactive" :maps-embed-key="maps_embed_key">
                <template #cta>
                    <ApplicationsClosedButton v-if="application_inactive" />
                    <template v-else>
                        <inertia-link
                            :href="registerUrl"
                            class="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
                            data-test="apply-button"
                        >
                            <font-awesome-icon :icon="faCircleArrowRight" class="h-4 w-4" aria-hidden="true" />
                            Apply Now
                        </inertia-link>
                        <!-- Sits where the mockup's Contact Property button would be; guests with an account sign in here. -->
                        <inertia-link
                            :href="loginUrl"
                            class="flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                            data-test="sign-in-link"
                        >
                            Sign In
                        </inertia-link>
                    </template>
                </template>
            </PropertyListing>

            <!-- RAC-10006: ESO opt-in modal — must be answered (no X / no
                 backdrop dismiss); user picks Upgrade or No thank you. -->
            <TransitionRoot as="template" :show="showEsoModal">
                <Dialog as="div" class="relative z-50" @close="() => {}">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-900/70 transition-opacity" />
                    </TransitionChild>
                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white px-10 pt-10 pb-10 text-center shadow-2xl transition-all sm:my-8">
                                    <DialogTitle as="h3" class="text-3xl font-bold text-gray-900 tracking-tight">Stand Out From the Crowd</DialogTitle>
                                    <p class="mt-4 text-sm text-gray-600">Show your landlord you're a reliable tenant by giving them:</p>
                                    <ul class="mt-5 space-y-1 text-left mx-auto inline-block">
                                        <li class="flex items-center text-base text-gray-800">
                                            <font-awesome-icon :icon="faCheck" class="h-4 w-4 mr-3 text-green-600 shrink-0" />
                                            Proof of Identity
                                        </li>
                                        <li class="flex items-center text-base text-gray-800">
                                            <font-awesome-icon :icon="faCheck" class="h-4 w-4 mr-3 text-green-600 shrink-0" />
                                            Income Verification
                                        </li>
                                        <li class="flex items-center text-base text-gray-800">
                                            <font-awesome-icon :icon="faCheck" class="h-4 w-4 mr-3 text-green-600 shrink-0" />
                                            Employment History
                                        </li>
                                        <li class="flex items-center text-base text-gray-800">
                                            <font-awesome-icon :icon="faCheck" class="h-4 w-4 mr-3 text-green-600 shrink-0" />
                                            Asset Summary
                                        </li>
                                    </ul>
                                    <p class="mt-7 text-sm text-gray-700">Upgrade your application for only ${{ eso_price }}.</p>
                                    <button type="button" @click="acceptEso" class="mt-6 inline-block rounded-md bg-rent-risk-blue-400 px-10 py-4 text-base font-bold uppercase tracking-wide text-white shadow-sm hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rent-risk-blue-400">
                                        Yes, Upgrade My Application
                                    </button>
                                    <button type="button" @click="declineEso" class="mt-5 text-sm text-gray-600 hover:text-gray-900 underline">
                                        No thank you, I want a basic application.
                                    </button>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </property-layout>
    </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { faCircleArrowRight } from '@fortawesome/pro-light-svg-icons'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { Head } from '@inertiajs/vue3'

import PropertyLayout from '@/Layouts/PropertyLayout.vue'
import ApplicationsClosedButton from './Partials/ApplicationsClosedButton.vue'
import PropertyListing from './Partials/PropertyListing.vue'

export default {
    components: {
        ApplicationsClosedButton,
        Dialog,
        DialogPanel,
        DialogTitle,
        FontAwesomeIcon,
        Head,
        PropertyLayout,
        PropertyListing,
        TransitionChild,
        TransitionRoot,
    },
    props: {
        property: Object,
        existingApplication: Object,
        is_main_applicant : Boolean,
        property_id : String,
        eso_price: {
            type: Number,
            default: 29,
        },
        application_inactive: {
            type: Boolean,
            default: false,
        },
        maps_embed_key: {
            type: String,
            default: '',
        },
        is_archived: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            esoOptIn: false,
            showEsoModal: false,
            faCheck,
            faCircleArrowRight,
        }
    },
    computed: {
        showEsoToggle() {
            const automatic =
                this.property.effective_auto_eso_active === true ||
                (this.property.effective_auto_eso_active === undefined && this.property.auto_eso_enabled);
            return !automatic && !this.property.is_agent_fee && !this.existingApplication;
        },
        registerUrl() {
            let url = this.route('register.from-property-page', this.property_id);
            if (this.esoOptIn) url += '?eso_optin=1';
            return url;
        },
        loginUrl() {
            let url = this.route('login.from-property-page', this.property_id);
            if (this.esoOptIn) url += '?eso_optin=1';
            return url;
        },
    },
    mounted() {
        // RAC-10006: auto-open the ESO opt-in modal once per browser session
        // for unauthenticated visitors who are eligible for ESO. The same
        // `esoOptIn` flag still drives the `?eso_optin=1` query param on the
        // register/login links — modal just replaces the inline checkbox UI.
        // Never on a closed listing, where there is nothing to apply to.
        if (!this.application_inactive && !this.$page.props.user && this.showEsoToggle) {
            const seenKey = 'rac10006_eso_modal_seen_' + this.property_id;
            try {
                if (sessionStorage.getItem(seenKey) === '1') {
                    return;
                }
            } catch {
                // sessionStorage may be unavailable (private mode) — fall through and show
            }
            this.showEsoModal = true;
        }
    },
    methods: {
        markEsoModalSeen() {
            try {
                sessionStorage.setItem('rac10006_eso_modal_seen_' + this.property_id, '1');
            } catch {
                // ignore — best-effort persistence
            }
        },
        acceptEso() {
            this.esoOptIn = true;
            this.showEsoModal = false;
            this.markEsoModalSeen();
        },
        declineEso() {
            this.esoOptIn = false;
            this.showEsoModal = false;
            this.markEsoModalSeen();
        },
    },
}
</script>
